import { Box, Flex, Image, Text, Link } from '@chakra-ui/react';
import { ExternalLink } from 'lucide-react';
import { motion, useAnimation } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface NewsArticleProps {
    title: string;
    date: string;
    subtitle: string;
    image: string;
    link: string;
}

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

export default function NewsArticle(props: NewsArticleProps) {
    const [isHovered, setIsHovered] = useState(false);
    const isMobile = window.innerWidth <= 768;

    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    useEffect(() => {
        if (inView) {
            controls.start('visible');
        }
    }, [controls, inView]);

    return (
        <MotionBox
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={{
                visible: { 
                    opacity: 1, 
                    x: 0,
                    y: 0,
                    transition: { duration: 0.6 } 
                },
                hidden: { 
                    opacity: 0, 
                    x: isMobile ? (Math.random() > 0.5 ? -40 : 40) : 0,
                    y: isMobile ? 0 : 20
                }
            }}
            width={{ base: '100%', md: '25%' }}
            flexShrink={0}
            position="relative"
        >
            <Link
                href={props.link}
                isExternal
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                _hover={{
                    textDecoration: "none",
                }}
            >
                <MotionFlex
                    direction="column"
                    backgroundColor="black.300"
                    borderRadius="md"
                    overflow="hidden"
                    height="100%"
                    position="relative"
                    animate={{
                        y: !isMobile && isHovered ? -12 : 0,
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                >
                    {/* Animated border effect */}
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        borderRadius="md"
                        opacity={!isMobile && isHovered ? 1 : 0}
                        transition="opacity 0.4s ease"
                        pointerEvents="none"
                        _before={{
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: 'md',
                            padding: '2px',
                            background: 'linear-gradient(45deg, #03A9F4, #00BCD4, #03A9F4)',
                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                        }}
                    />

                    {/* Glow effect */}
                    <Box
                        position="absolute"
                        top="-50%"
                        left="-50%"
                        right="-50%"
                        bottom="-50%"
                        background="radial-gradient(circle, rgba(3, 169, 244, 0.4) 0%, transparent 70%)"
                        opacity={!isMobile && isHovered ? 1 : 0}
                        transition="opacity 0.4s ease"
                        pointerEvents="none"
                        filter="blur(20px)"
                    />

                    <Box
                        width="100%"
                        height="200px"
                        overflow="hidden"
                        position="relative"
                    >
                        <motion.div
                            animate={{
                                scale: !isMobile && isHovered ? 1.1 : 1,
                            }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            style={{ width: '100%', height: '100%' }}
                        >
                            <Image
                                src={props.image}
                                alt={props.title}
                                width="100%"
                                height="100%"
                                objectFit="cover"
                            />
                        </motion.div>
                        
                        {/* Image overlay gradient on hover */}
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            background="linear-gradient(180deg, transparent 0%, rgba(3, 169, 244, 0.2) 100%)"
                            opacity={!isMobile && isHovered ? 1 : 0}
                            transition="opacity 0.4s ease"
                        />
                    </Box>

                    <Flex
                        direction="column"
                        padding={5}
                        flex={1}
                        position="relative"
                        zIndex={1}
                    >
                        <Flex alignItems="center" gap={2} marginBottom={2}>
                            <motion.div
                                animate={{
                                    x: !isMobile && isHovered ? 4 : 0,
                                }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <Text
                                    fontSize="lg"
                                    fontFamily="Source Code Pro Variable"
                                    fontWeight="600"
                                    color={!isMobile && isHovered ? "blue.300" : "white.200"}
                                    lineHeight="1.3"
                                    transition="color 0.3s ease"
                                >
                                    {props.title}
                                </Text>
                                <motion.div
                                    animate={{
                                        x: !isMobile && isHovered ? 4 : 0,
                                        rotate: !isMobile && isHovered ? -15 : 0,
                                    }}
                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                >
                                    <ExternalLink
                                        size={16}
                                        color="#03A9F4"
                                        style={{ flexShrink: 0 }}
                                    />
                                </motion.div>
                            </motion.div>
                        </Flex>
                        <Text
                            fontSize="sm"
                            fontFamily="Source Code Pro Variable"
                            fontWeight="400"
                            color="blue.300"
                            marginBottom={3}
                        >
                            {props.date}
                        </Text>
                        <Text
                            fontSize="sm"
                            fontFamily="Source Code Pro Variable"
                            fontWeight="400"
                            color="white.300"
                            lineHeight="1.5"
                            noOfLines={3}
                        >
                            {props.subtitle}
                        </Text>
                    </Flex>
                </MotionFlex>
            </Link>
        </MotionBox>
    );
}
